<template>
  <v-btn
    :color="computedColor"
    :outlined="outlined"
    :text="text || plain"
    :plain="plain"
    :icon="finalIconAttr"
    :fab="fab"
    :tile="tile"
    :rounded="rounded"
    :small="small"
    :large="large"
    :x-small="xSmall"
    :x-large="xLarge"
    :disabled="disabled"
    :loading="loading"
    :block="block"
    :depressed="depressed"
    v-bind="$attrs"
    v-on="$listeners"
    class="base-button"
    :class="computedClasses"
  >
    <!-- 1. Icon Only Mode (MDI) -->
    <v-icon v-if="renderIconOnly">{{ icon }}</v-icon>

    <!-- 2. Image Mode -->
    <img
      v-if="image"
      :src="image"
      :alt="imageAlt"
      :style="imageStyle"
      :class="{'mr-2': hasTextContent}" 
    />

    <!-- 3. Prepend Icon -->
    <v-icon
      v-if="!renderIconOnly && prependIcon"
      left
      :size="iconSize"
    >
      {{ prependIcon }}
    </v-icon>

    <!-- 4. Default Content (Label or Slot) -->
    <span v-if="!renderIconOnly && hasTextContent">
      <slot>{{ label }}</slot>
    </span>

    <!-- 5. Append Icon -->
    <v-icon
      v-if="!renderIconOnly && appendIcon"
      right
      :size="iconSize"
    >
      {{ appendIcon }}
    </v-icon>
  </v-btn>
</template>

<script>
/**
 * base-button
 * Wrapper for v-btn to handle common button patterns and application styles:
 * - Primary/Secondary/Reject/Apply variants (mapped to global CSS classes)
 * - Outlined / Text / Plain styles
 * - Icon-only buttons
 * - Buttons with images or icons (prepend/append)
 */
export default {
  name: "BaseButton",
  inheritAttrs: false, // Pass attributes to v-btn manually
  props: {
    // Content Props
    label: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
      note: "Icon name for icon-only button (MDI)",
    },
    prependIcon: {
      type: String,
      default: "",
    },
    appendIcon: {
      type: String,
      default: "",
    },
    image: {
      type: String, // URL/Path to image
      default: "",
    },
    imageAlt: {
      type: String,
      default: "button-image",
    },
    imageWidth: {
      type: [Number, String],
      default: 16,
    },

    // Style Variants
    primary: Boolean,
    secondary: Boolean,
    success: Boolean,
    error: Boolean,
    warning: Boolean,
    info: Boolean,
    reject: Boolean, // Added for rejectBtn style (outlined)
    rejectFilled: Boolean, // Added for rejectFilledBtn style (filled)
    apply: Boolean,  // Added for applyBtn style
    
    // Appearance
    outlined: Boolean,
    text: Boolean,
    plain: Boolean,
    tile: Boolean,
    rounded: Boolean,
    depressed: Boolean,
    fab: Boolean,
    iconStyle: { // Explicitly ask for icon-button styling (round, transparent)
      type: Boolean,
      default: false
    },
    
    // Size
    small: Boolean,
    xSmall: Boolean,
    large: Boolean,
    xLarge: Boolean,

    // State
    disabled: Boolean,
    loading: Boolean,
    block: Boolean,

    // Colors override
    color: {
      type: String,
      default: "",
    },
  },
  computed: {
    computedClasses() {
      return {
        'primaryBtn': this.primary,
        'secondaryBtn': this.secondary,
        'rejectBtn': this.reject || this.error,
        'rejectFilledBtn': this.rejectFilled,
        'applyBtn': this.apply,
      }
    },
    computedColor() {
      if (this.color) return this.color;
      // If we are using class-based styles (primary/secondary/apply/reject props), 
      // we might not want to set title color via Vuetify prop if the class handles it with !important.
      // However, for success/info/warning which might not have specific classes in _globals.scss provided,
      // we can fallback to Vuetify colors.
      
      if (this.success) return "success";
      if (this.warning) return "warning";
      if (this.info) return "info";
      // Note: primary/secondary/error are handled by classes now, 
      // but keeping them null here lets Vuetify or class take over.
      
      return undefined; 
    },
    hasTextContent() {
      // Check if there is label text or slot content
      return !!this.label || (this.$slots.default && this.$slots.default.length > 0);
    },
    renderIconOnly() {
       // Only render MDI icon provided in 'icon' prop IF we don't have other content 
       // AND we aren't just using iconStyle for an image-button
       return !!this.icon && !this.hasTextContent;
    },
    finalIconAttr() {
      // Pass 'icon' prop to v-btn if:
      // 1. We are rendering just an MDI icon
      // 2. User explicitly requested icon-style (e.g. for image-only button)
      return this.renderIconOnly || this.iconStyle;
    },
    imageStyle() {
      const width = String(this.imageWidth);
      const finalWidth = /^[0-9.]+$/.test(width) ? `${width}px` : width;
      return {
        height: 'auto',
        width: finalWidth,
        maxHeight: '100%'
      };
    },
    iconSize() {
      if (this.xSmall) return '16px';
      if (this.small) return '18px';
      if (this.large) return '24px';
      if (this.xLarge) return '28px';
      return undefined; // Default
    }
  },
};
</script>

<style scoped>
.base-button {
  text-transform: none; /* Often requested to avoid UPPERCASE default in Material Design */
  font-weight: 500;
  letter-spacing: 0.5px;
}
</style>
